import { useState, type FormEvent } from "react";
import { contactSubjects } from "@/mocks/contato";

const FORM_ENDPOINT = "https://readdy.ai/api/form/datdkur2asjjtt1ske5g";

type StatusState = "idle" | "loading" | "success" | "error";

interface FormFeedback {
  state: StatusState;
  message: string;
}

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function ContactForm() {
  const [feedback, setFeedback] = useState<FormFeedback>({ state: "idle", message: "" });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFeedback({
        state: "success",
        message: "Recebemos a sua mensagem. Em breve a nossa equipe entrará em contato.",
      });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value !== "string" || value.trim() === "") return;
      payload.append(key, value);
    });

    setFeedback({ state: "loading", message: "" });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMessage = String(
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? ""
      ).trim();

      const isSpam = /spam/i.test(serverMessage);
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isSuccess) {
        setFeedback({
          state: "error",
          message:
            serverMessage ||
            "Não foi possível enviar a sua mensagem agora. Tente novamente em instantes.",
        });
        return;
      }

      setFeedback({
        state: "success",
        message: "Recebemos a sua mensagem. Em breve a nossa equipe entrará em contato.",
      });
      form.reset();
    } catch {
      setFeedback({
        state: "error",
        message:
          "Não foi possível enviar a sua mensagem. Verifique a sua conexão e tente novamente.",
      });
    }
  }

  const loading = feedback.state === "loading";

  return (
    <form
      id="contact-form-nxhost"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="mb-6">
        <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
          Envie uma mensagem
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          Preencha o formulário e a nossa equipe retorna o contato o mais rápido possível.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="nome" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Nome completo
          </label>
          <input
            id="nome"
            name="nome"
            type="text"
            required
            autoComplete="name"
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="voce@empresa.com.br"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="telefone" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Telefone / WhatsApp
          </label>
          <input
            id="telefone"
            name="telefone"
            type="tel"
            autoComplete="tel"
            placeholder="(54) 99999-9999"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="empresa" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Empresa
          </label>
          <input
            id="empresa"
            name="empresa"
            type="text"
            autoComplete="organization"
            placeholder="Nome da sua empresa"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="assunto" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Assunto
          </label>
          <select id="assunto" name="assunto" defaultValue="" required className={inputClass}>
            <option value="" disabled>
              Selecione um assunto
            </option>
            {contactSubjects.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="mensagem" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Mensagem
          </label>
          <textarea
            id="mensagem"
            name="mensagem"
            required
            maxLength={500}
            rows={5}
            placeholder="Conte o que você precisa..."
            className={`${inputClass} resize-none`}
          ></textarea>
          <p className="mt-1.5 text-xs text-foreground-500">Máximo de 500 caracteres.</p>
        </div>
      </div>

      <div className="nx-honeypot-field" aria-hidden="true">
        <label htmlFor="website_alt">Não preencha este campo</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {feedback.state === "error" ? (
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-foreground-300 bg-background-100 px-4 py-3">
          <i className="ri-error-warning-line mt-0.5 text-lg text-foreground-700" aria-hidden="true"></i>
          <p className="text-sm text-foreground-800">{feedback.message}</p>
        </div>
      ) : null}

      {feedback.state === "success" ? (
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-700" aria-hidden="true"></i>
          <p className="text-sm text-secondary-900">{feedback.message}</p>
        </div>
      ) : null}

      <button
        type="submit"
        disabled={loading}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {loading ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
            Enviando...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-lg" aria-hidden="true"></i>
            Enviar mensagem
          </>
        )}
      </button>
    </form>
  );
}